import { useTranslation } from "react-i18next";
import Toolbar from "@mui/material/Toolbar";

import { CountResponse } from "shared/api/api";
import { formatNumber } from "shared/locale/format";

import TableFilterButton from "pages/Dashboard/components/EventTables/TableFilterButton";

import Typography from "features/components/Typography";
import DownloadAction, {
  DownloadActionProps,
} from "features/ui/DownloadAction";

export type DownloadConfig = DownloadActionProps;

export interface FilterConfig {
  activeFilterCount: number;
  onClick: () => void;
  dataTestId?: string;
}

interface Props {
  children?: React.ReactNode;
  rightContent?: React.ReactNode;
  countData?: CountResponse;
  download?: DownloadConfig;
  filters?: FilterConfig;
  countLabel?: string;
  countLabelPlural?: string;
  i18nCountKey?: string;
}

/**
 * Generic toolbar component for MaterialTable with built-in support for
 * filters and download actions (or other stuff in the future).
 */
const CustomTableToolbar = ({
  children,
  rightContent,
  download,
  filters,
  countData,
  countLabel,
  countLabelPlural,
  i18nCountKey,
}: Props) => {
  const { t } = useTranslation();
  const count = countData?.count || 0;

  return (
    <Toolbar className="!min-h-[50px] !max-h-[50px] border-0 bg-white rounded-t-lg flex flex-row !px-2 overflow-hidden border-b-1 border-gray-100">
      <div className="flex flex-row items-center justify-start w-full gap-4">
        {countData && (
          <Typography variant="h5" className="!ml-[6px]">
            {formatNumber(count, 0)}{" "}
            {i18nCountKey && t(i18nCountKey, { count })}
            {!i18nCountKey &&
              ((count === 1 ? countLabel : countLabelPlural) ||
                t("common:items", { count }))}
          </Typography>
        )}
        {children}
      </div>
      <div className="ml-auto flex flex-row gap-2">
        {rightContent}
        {filters && (
          <TableFilterButton
            activeFilterCount={filters.activeFilterCount}
            onClick={filters.onClick}
            dataTestId={filters.dataTestId}
          />
        )}
        {download && <DownloadAction {...download} />}
      </div>
    </Toolbar>
  );
};

export default CustomTableToolbar;
